import { useMemo, useState, useEffect, useRef } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router';

import Cookies from 'js-cookie';
import { FFSignInButton } from '../signin';
import { Loading } from '../loading';
import { Textarea, Input, Button, Check } from '../input';
import { Error } from '../error';
import { Modal, closeHook } from '../modal';
import { TableFromObject } from '../table';
import { track } from '../track';
import { useGlobal, useJob } from '../../state';
import { styles } from '../../constants';
import { JobCard } from './JobCard';

const UrlPrompt = ({ onPlan }) => {
  const { fox } = useGlobal();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    plan();
  };

  const plan = async () => {
    setLoading(true);
    try {
      const out = await fox.call('POST', '/api/plan', { url });
      console.log('Plan out:', out);
      onPlan(out);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <form onSubmit={handleSubmit}>
        <Input
          disabled={loading}
          style={{ width: '100%' }}
          placeholder="Enter a URL starting with https://..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <div style={{ position: 'absolute', right: 4, bottom: 4 }}>
          <Button loading={loading} disabled={loading} style={{ width: 28, height: 28, padding: 0 }}>
            <FaArrowRight size={14} />
          </Button>
        </div>
      </form>
    </div>
  );
};

const UrlList = ({ label = 'URLs', subLabel = 'Extract data from these pages', urls = [] }) => {
  const maxUrls = 10;
  const rest = urls.length - maxUrls;

  if (!urls.length) {
    return null;
  }

  return (
    <div>
      <div style={styles.smallCaps}>{label}</div>
      <div style={{ ...styles.smallText, marginBottom: 5 }}>{subLabel}</div>
      <div style={{ fontSize: 14 }}>
        {urls.slice(0, maxUrls).map((url) => (
          <div key={url}>{url}</div>
        ))}
        {rest > 0 && (
          <div>
            ({rest} more URL{rest == 1 ? '' : 's'} hidden)
          </div>
        )}
      </div>
    </div>
  );
};

const ItemTemplate = ({ template }) => {
  if (!template) {
    return null;
  }

  return (
    <div>
      <div style={styles.smallCaps}>Item Template</div>
      <div style={{ ...styles.smallText, marginBottom: 5 }}>Convert pages into this format</div>
      {typeof template == 'string' ? (
        <div style={{ fontSize: 14 }}>{template}</div>
      ) : (
        <TableFromObject obj={template} cellStyle={{ fontSize: 14 }} />
      )}
    </div>
  );
};

const ExtractPlan = ({ plan }) => {
  const urls = plan.params.urls || (plan.params.url ? [plan.params.url] : []);

  return (
    <div style={{ display: 'flex', gap: 20, flexDirection: 'column' }}>
      <UrlList urls={urls} />
      <ItemTemplate template={plan.params.template} />
    </div>
  );
};

const CrawlTargets = ({ plan }) => {
  return <UrlList label="Start URLs" subLabel="Begin crawling from these pages" urls={plan.params.startUrls || []} />;
};

const CrawlRule = ({ plan }) => {
  if (!plan.params.query && !plan.params.pattern) {
    return null;
  }

  const label = plan.params.query ? 'Find URLs' : 'URL Pattern';
  const subLabel = plan.params.query
    ? 'Look for pages matching this search query'
    : 'Look for pages matching this URL pattern';
  const value = plan.params.query || plan.params.pattern;

  return (
    <div>
      <div style={styles.smallCaps}>{label}</div>
      <div style={{ ...styles.smallText, marginBottom: 5 }}>{subLabel}</div>
      <div style={{ fontSize: 14 }}>{value}</div>
    </div>
  );
};

const CrawlPlan = ({ plan }) => (
  <div style={{ display: 'flex', gap: 20, flexDirection: 'column' }}>
    <CrawlTargets plan={plan} />
    <CrawlRule plan={plan} />
  </div>
);

const ScrapePlan = ({ plan }) => (
  <div style={{ display: 'flex', gap: 20, flexDirection: 'column' }}>
    <CrawlPlan plan={plan} />
    <ItemTemplate template={plan.params.template} />
  </div>
);

const PlanProgress = ({ jobId }) => {
  const { job } = useJob(jobId);

  const children = job?.children || [];
  const nodes = children
    .filter((it) => it.label == 'plan')
    .filter((it) => ['visit', 'runCompletion'].includes(it.name))
    .map((it) => <JobCard jobId={it.jobId} />);

  return (
    <div>
      <div style={{ borderx: '1px solid red' }}>{nodes}</div>

      {/*
      PlanProgress: {JSON.stringify(children)}
      */}
    </div>
  );
};

const Plan = ({ jobId, prompt, onClose, onRun, planLoading }) => {
  const navigate = useNavigate();
  const close = closeHook();
  const { apiKey } = useGlobal();
  const [loading, setLoading] = useState();
  const [showSignIn, setShowSignIn] = useState();
  const [isTestRun, setIsTestRun] = useState();
  const { job } = useJob(jobId);

  const plan = job.results?.plan || {};
  const method = plan?.method || 'none';

  const fn =
    {
      extract: () => <ExtractPlan plan={plan} />,
      crawl: () => <CrawlPlan plan={plan} />,
      scrape: () => <ScrapePlan plan={plan} />,
    }[method] || (() => <div>...</div>);

  useEffect(() => {
    setShowSignIn(!apiKey);
  }, [apiKey]);

  useEffect(() => {}, [prompt, isTestRun]);

  const getExtraParams = () => {
    const extraParams = {};
    if (isTestRun) {
      extraParams.maxVisits = 20;
      extraParams.maxExtracts = 20;
    }
    return extraParams;
  };

  const handleSaveParams = () => {
    Cookies.set(
      'handleAgent',
      JSON.stringify({
        plan,
        prompt,
        extraParams: getExtraParams(),
      }),
    );
  };

  const handleRun = () => {
    if (!apiKey) {
      setShowSignIn(true);
      return;
    }
    setLoading(true);
    track('agent/run', { prompt });
    const extraParams = getExtraParams();
    onRun({ plan, prompt, extraParams: getExtraParams() });
  };

  let body;
  if (job?.state == 'completed') {
    body = (
      <div>
        <div style={{ fontWeight: 'bold', color: '#555' }}>FetchFox has generated a scraping plan from your prompt</div>
        <div
          style={{
            marginTop: 10,
            border: '2px solid #ccc',
            padding: 20,
            borderRadius: 5,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {fn()}

          <Check useSwitch size="md" value={isTestRun} onChange={setIsTestRun}>
            Do a small test run (uses fewer credits)
          </Check>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button style={{ flex: 1 }} grayFlat onClick={() => onClose()}>
              Edit Prompt
            </Button>
            {!showSignIn && (
              <Button style={{ flex: 1 }} onClick={handleRun} loading={loading}>
                Run
              </Button>
            )}
            {showSignIn && (
              <FFSignInButton onClick={handleSaveParams} style={{ flex: 1 }}>
                Sign In to Run
              </FFSignInButton>
            )}
          </div>
        </div>
      </div>
    );
  } else if (job?.state == 'active') {
    body = (
      <div>
        <Loading>Planning...</Loading>
        <PlanProgress jobId={jobId} />
      </div>
    );
  }

  return <div>{body}</div>;

  return (
    <Modal close={close.signal} startOpen noClose onClose={onClose} innerStyle={{ padding: 0 }}>
      <div></div>

      <div style={{ textAlign: 'left', padding: 15, width: 640 }}>{body}</div>
    </Modal>
  );
};

export const AgentPrompt = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sdk, fox } = useGlobal();
  const [prompt, setPrompt] = useState(props.prompt || '');
  const [plan, setPlan] = useState('');
  const [jobId, setJobId] = useState();
  const [showPlan, setShowPlan] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState();

  const jobIdRef = useRef();

  useEffect(() => {
    if (!props.autoRun) {
      return;
    }

    if (!sdk) {
      return;
    }

    if (jobIdRef.current) {
      return;
    }

    if (!prompt) {
      return;
    }

    jobIdRef.current = true;
    console.log('Auto run!', jobIdRef.current);

    runPrompt(prompt);
  }, [sdk]);

  const isMac = useMemo(() => {
    try {
      return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
    } catch {
      return false;
    }
  }, []);

  const examples = {
    extractPokemon: {
      name: 'extract pikachu',
      prompt: 'scrape pokemon name, number, and evolutions on https://pokemondb.net/pokedex/pikachu',
    },
    crawlPokemon: {
      name: 'find pokemon urls',
      prompt: 'find URLs of pokemon pages on https://pokemondb.net/',
    },
    scrapePokemon: {
      name: 'scrape lots of pokemon',
      prompt:
        'find URLs of pokemon on https://pokemondb.net/ and then get their name, number, and evolutions. limit your scraping to 10 page visits, and a max of 20 extractions',
    },
  };

  const exampleButton = (key) => (
    <Button key={key} small gray trans onClick={() => handleExample(examples[key].prompt)}>
      {examples[key].name}
    </Button>
  );

  const run = async (prompt_) => {
    if (location.pathname == '/new') {
      runPrompt(prompt_);
    } else {
      Cookies.set('latestPrompt', prompt_);
      navigate(`/new`);
    }
  };

  const runPrompt = (prompt_) => {
    const params = { prompt: prompt_ };
    track('agent/plan', params);
    setJobId(null);
    setLoading(true);
    sdk.plan
      .detach(params)
      .then((job) => {
        console.log('plan job id:', job.id, job);
        setJobId(job.id);
        setShowPlan(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // setLoading(true);
  // setShowPlan(true);
  // setJobId(null);

  // console.log('Run plan:', prompt_);

  // try {
  //   const params = {
  //     prompt: prompt_,
  //   };
  //   track('agent/plan', params);
  //   const job = await sdk.plan.detach(params);
  //   console.log('Run plan job:', job.id);
  //   Cookies.set('latestPrompt', prompt_);
  //   navigate(`/new?plan=1`);
  //   // setJobId(job.id);
  // } finally {
  //   setLoading(false);
  // }
  // };

  const handleClose = () => {
    setShowPlan(false);
    setPlan(null);
    setLoading(false);
  };

  const handleRunPlan = ({ plan: plan_, prompt: prompt_, extraParams }) => {
    console.log('handleRunPlan', plan, prompt);
    fox
      .agent({
        prompt: prompt_,
        ...extraParams,
        detach: true,
      })
      .then((out) => {
        console.log('Run plan gave out:', out);
        navigate(`/agents/${out.jobId}`);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (loading) {
      return;
    }

    const m = prompt.match(/https:\/\/.{5}/);
    if (!m) {
      setError('Include at least one URL starting with "https://..."');
      return;
    }

    run(prompt);
  };

  const handleKeyDown = (e) => {
    if (e.key == 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleExample = (s) => {
    setPrompt(s);
    setLoading(true);
    setTimeout(() => run(s), 400);
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit}>
        <div style={{ position: 'relative' }}>
          <Textarea
            style={{ width: '100%' }}
            minRows={3}
            type="text"
            placeholder={`What would you like to scrape?
- Include at least one URL starting with https://...
- Describe the data you want`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={showPlan}
          />
          <div style={{ position: 'absolute', right: 6, bottom: 10 }}>
            <Button loading={loading} disabled={showPlan} style={{ width: 30, height: 30, padding: 0 }}>
              <FaArrowRight size={14} />
            </Button>
          </div>
        </div>
      </form>

      <Error message={error} small />

      <div
        style={{
          display: 'flex',
          gap: 5,
          alignItems: 'center',
          justifyContent: 'center',
          visibility: showPlan ? 'hidden' : null,
        }}
      >
        <div style={{ fontSize: 12, color: '#888' }}>{isMac ? '⌘ + enter to submit' : 'ctrl + enter to submit'}</div>

        <div style={{ flexGrow: 1 }}></div>

        <div style={{ fontSize: 12, color: '#888' }}>examples:</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {Object.keys(examples).map((key) => exampleButton(key))}
        </div>
      </div>

      {showPlan && (
        <div>
          <Plan
            jobId={jobId}
            prompt={prompt}
            plan={plan}
            planLoading={loading}
            onClose={handleClose}
            onRun={handleRunPlan}
          />
        </div>
      )}
    </div>
  );
};
